import React, { useEffect, useState } from 'react';
import { Checkbox } from '@mui/material';
import { useHttpContext } from '@/common/hooks/HttpContext';
import { toasts } from '@/common/toasts';
import { AdminOrganizationLocation, Maybe } from '@/common/types';
import { ImportParticipantsModal } from '@/components/Organization/AdminOrganization/ImportParticipantsModal';
import { OrganizationsTable } from '@/components/Organization/OrganizationsTable/OrganizationsTable';
import { LoadingSpinner } from '@/components/common/LoadingSpinner/LoadingSpinner';
import { Button } from '@/components/common/button/button';
import { resultIsError } from '@/services/HttpService';

interface AdminOrganizationLocationsProps {
    organizationId: number;
    isSaving: boolean;
    pendingUpdates: Map<number, boolean>;
    setPendingUpdates: React.Dispatch<React.SetStateAction<Map<number, boolean>>>;
}

const formatAddress = (location: AdminOrganizationLocation) => {
    const cityStateSeparator = location.city && location.stateCode ? ', ' : ' ';
    const address1Separator = !location.address2 ? ', ' : ' ';

    return (
        <>
            {location.address1 && location.address1 + address1Separator}
            {location.address2 && location.address2 + ', '}
            {location.city && location.city + cityStateSeparator}
            {location.stateCode && location.stateCode + ' '}
            {location.zipCode && location.zipCode + ' '}
        </>
    );
};

export const AdminOrganizationLocations = (props: AdminOrganizationLocationsProps) => {
    const { organizationId, isSaving, pendingUpdates, setPendingUpdates } = props;
    const { httpService } = useHttpContext();
    const { errorToast } = toasts;
    const [isLoading, setIsLoading] = useState(false);
    const [locations, setLocations] = useState<AdminOrganizationLocation[]>([]);
    const [locationToImport, setLocationToImport] = useState<Maybe<AdminOrganizationLocation>>(undefined);

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            const res = await httpService.getAdminScanLocationsForOrganization(organizationId);
            if (resultIsError(res)) {
                errorToast('Could not fetch organization locations.');
            } else {
                setLocations(res);
            }
            setIsLoading(false);
        })();
    }, [organizationId, httpService, errorToast]);

    const handleSupportsParticipantDtlsChange = (location: AdminOrganizationLocation, checked: boolean) => {
        setPendingUpdates((prev) => {
            const next = new Map(prev);
            if (checked === location.supportsParticipantDtls) {
                next.delete(location.id);
            } else {
                next.set(location.id, checked);
            }
            return next;
        });
    };

    const columnStructure = [
        {
            Header: 'ID',
            accessor: 'id',
            width: 70,
        },
        {
            Header: 'Name',
            accessor: 'name',
        },
        {
            Header: 'Address',
            id: 'address',
            accessor: (location: AdminOrganizationLocation) => location,
            Cell: ({ value }: { value: AdminOrganizationLocation }) => formatAddress(value),
        },
        {
            Header: 'Timezone',
            accessor: 'timezone',
        },
        {
            Header: 'Supports Participant Details',
            accessor: (location: AdminOrganizationLocation) => location,
            id: 'supportsParticipantDtls',
            Cell: ({ value }: { value: AdminOrganizationLocation }) => {
                const checked = pendingUpdates.get(value.id) ?? value.supportsParticipantDtls;
                return (
                    <Checkbox
                        checked={checked}
                        onChange={(e) => handleSupportsParticipantDtlsChange(value, e.target.checked)}
                        disabled={isSaving}
                    />
                );
            },
        },
        {
            Header: 'Action',
            accessor: (location: AdminOrganizationLocation) => location,
            id: 'action',
            Cell: ({ value }: { value: AdminOrganizationLocation }) => (
                <Button variant="tertiary" size="small" onClick={() => setLocationToImport(value)}>
                    Import Participants
                </Button>
            ),
        },
    ];

    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (locations.length === 0) {
        return null;
    }

    return (
        <>
            <OrganizationsTable columnStructure={columnStructure} data={locations} />
            {locationToImport && (
                <ImportParticipantsModal
                    organizationId={organizationId}
                    location={locationToImport}
                    onClose={() => setLocationToImport(undefined)}
                />
            )}
        </>
    );
};
